/**
 * 요금 — **숫자의 원본이 이 파일 하나다.** (docs/decisions/0219 · 0220 · docs/pricing.md)
 *
 * 문서(`docs/pricing.md`)는 왜 그 숫자인지를 말하고, 숫자 자체는 여기 산다. 서버(구독 판정)·앱(상품
 * 목록)·웹(안내)이 이 파일을 읽는다 — 셋이 각자 숫자를 적으면 가격을 고치는 날 한쪽만 바뀐다.
 *
 * ─── 왜 "인원 구간 정액"인가 (2026-10-09 소유자 결정, 0220) ───
 * 주 고객은 사장 + 알바 몇 명의 자영업자다. 인당 가격을 "무료 5명을 넘는 10명분" 상품에 실었더니(0219)
 * 6명 가게가 15명 가게와 같은 값을 냈다 — 돈을 내는 가게 대부분이 바로 그 구간이다. 그래서 가게 크기의
 * **정액**으로 바꿨다: 2명까지 무료, 그 위는 "최대 N명" 상품 하나가 그 인원 전부를 덮는다.
 *
 * 금액은 전부 **원 · VAT 별도**다. 스토어는 VAT 를 포함해 표시하므로 상품 가격은 이 값의 1.1배로 등록한다
 * (`storePriceWon`). 화면에는 스토어가 준 `priceString` 을 그대로 쓴다 — 환율·반올림을 앱이 재현하지 않는다.
 */

/**
 * RevenueCat 웹훅의 HTTP 프리픽스. `ESIGN_WEBHOOK_PREFIX` 와 같은 논거로 contracts 가 소유한다(규칙 20·44) —
 * 서버가 이 경로에 붙고, Vite proxy 와 RevenueCat 대시보드의 등록 주소가 이 값을 쓴다.
 */
export const BILLING_WEBHOOK_PREFIX = '/billing/revenuecat';

/**
 * 재직 인원이 이 수 이하인 회사는 무료다 — 사장 + 알바 한 명(2026-10-09 소유자 결정).
 * 넘으면 그 인원 **전부**를 덮는 구간을 산다(빼고 세지 않는다 — 정액이라 뺄 것이 없다).
 */
export const FREE_HEADCOUNT = 2;

/** 연간 결제 할인 — 10%(2026-10-08 소유자 결정). */
export const ANNUAL_DISCOUNT = 0.1;

/** 스토어 표시 가격에 붙는 부가세. */
export const VAT_RATE = 0.1;

/**
 * 구간 — 상품 하나가 덮는 **최대 재직 인원**. 10명 아래를 잘게 나눈 것은 알바를 한두 명 더 쓰는 가게가
 * 한 칸씩 오르게 하려는 것이다. 50명을 넘는 회사는 상품이 아니라 상담이다.
 */
export const HEADCOUNT_TIERS = [3, 5, 7, 10, 15, 20, 30, 50] as const;
export type HeadcountTier = (typeof HEADCOUNT_TIERS)[number];

/**
 * 플랜 — 무엇을 사는가. 키는 상품 id 의 한 조각이라 **바꾸면 이미 판 상품이 고아가 된다.**
 *
 * 둘은 스토어의 **같은 구독 그룹**이다 — 한 회사가 하나만 갖고, 근태만 → 전부로 바꾸면 스토어가 차액을 정산한다.
 * 자영업자에게는 고를 것이 적을수록 좋아서 둘뿐이다: 근태만 필요한 가게 · 다 쓰는 가게.
 *
 * 월 가격(원 · VAT 별도)은 "인당 천 원" 줄에 맞췄다 — 전부 = 최대 N명 × 1,000 − 100, 근태 = N × 400 − 100
 * (최대 10명 9,900원 · 3,900원). 줄은 결정의 모양이고 원본은 아래 표다: 한 칸만 고치는 날 식을 고치지 않는다.
 */
export const BILLING_PLANS = {
  time: {
    modules: ['time'],
    monthlyWon: { 3: 1_100, 5: 1_900, 7: 2_700, 10: 3_900, 15: 5_900, 20: 7_900, 30: 11_900, 50: 19_900 },
  },
  all: {
    modules: ['time', 'pay', 'esign'],
    monthlyWon: { 3: 2_900, 5: 4_900, 7: 6_900, 10: 9_900, 15: 14_900, 20: 19_900, 30: 29_900, 50: 49_900 },
  },
} as const satisfies Record<string, { modules: readonly string[]; monthlyWon: Record<HeadcountTier, number> }>;

export type BillingPlan = keyof typeof BILLING_PLANS;
export const BILLING_PLAN_KEYS = Object.keys(BILLING_PLANS) as BillingPlan[];

export const BILLING_PERIODS = ['monthly', 'yearly'] as const;
export type BillingPeriod = (typeof BILLING_PERIODS)[number];

export interface StoreProduct {
  readonly plan: BillingPlan;
  /** 이 상품이 덮는 최대 재직 인원. */
  readonly upTo: HeadcountTier;
  readonly period: BillingPeriod;
}

/** 재직 인원이 무료 구간 안인가. */
export const isFreeHeadcount = (headcount: number): boolean => headcount <= FREE_HEADCOUNT;

/** 그 인원을 덮는 가장 작은 구간. 50명을 넘으면 `null` — 상담이다. */
export function tierFor(headcount: number): HeadcountTier | null {
  return HEADCOUNT_TIERS.find((t) => t >= headcount) ?? null;
}

/** 한 결제 주기의 가격(원 · VAT 별도). 연간은 12개월에서 할인을 뺀 값이고 원 단위로 내린다. */
export function priceWon({ plan, upTo, period }: StoreProduct): number {
  const monthly = BILLING_PLANS[plan].monthlyWon[upTo];
  return period === 'monthly' ? monthly : Math.floor(monthly * 12 * (1 - ANNUAL_DISCOUNT));
}

/** 스토어에 등록하는 가격(VAT 포함). 스토어 가격표에 같은 값이 없으면 가장 가까운 칸을 고른다 — 그건 콘솔의 일이다. */
export const storePriceWon = (p: StoreProduct): number => Math.round(priceWon(p) * (1 + VAT_RATE));

/**
 * 상품 id. **App Store 의 product id 이자 RevenueCat 의 상품 식별자**다. `s<N>` 의 N 은 최대 재직 인원이다.
 * Google Play 는 구독(`<plan>_s<N>`) + 기본 요금제(`monthly`·`yearly`)로 나뉘고 RevenueCat 은
 * 그것을 `<구독>:<기본 요금제>` 로 부른다 — `parseStoreProductId` 가 둘 다 읽는다.
 */
export const APP_STORE_PRODUCT_PREFIX = 'com.worhythm.app';

export function appStoreProductId({ plan, upTo, period }: StoreProduct): string {
  return `${APP_STORE_PRODUCT_PREFIX}.${plan}.s${upTo}.${period}`;
}

export function playProductId({ plan, upTo, period }: StoreProduct): string {
  return `${plan}_s${upTo}:${period}`;
}

const isPlan = (v: string): v is BillingPlan => Object.hasOwn(BILLING_PLANS, v);
const isTier = (v: number): v is HeadcountTier => (HEADCOUNT_TIERS as readonly number[]).includes(v);
const isPeriod = (v: string): v is BillingPeriod => (BILLING_PERIODS as readonly string[]).includes(v);

/** 상품 id → 무엇을 산 것인가. 모르는 모양이면 `null` — 지어내지 않는다(서버는 그것을 Error 로 남긴다). */
export function parseStoreProductId(id: string): StoreProduct | null {
  const apple = new RegExp(`^${APP_STORE_PRODUCT_PREFIX.replace(/\./g, '\\.')}\\.([a-z]+)\\.s(\\d+)\\.([a-z]+)$`).exec(id);
  const play = /^([a-z]+)_s(\d+):([a-z]+)$/.exec(id);
  const m = apple ?? play;
  if (!m) return null;
  const [, plan, upTo, period] = m as unknown as [string, string, string, string];
  const n = Number(upTo);
  return isPlan(plan) && isTier(n) && isPeriod(period) ? { plan, upTo: n, period } : null;
}

/** 스토어에 등록해야 하는 상품 전부 — 콘솔 설정의 체크리스트이자 앱이 스토어에 물을 목록. */
export function allStoreProducts(): StoreProduct[] {
  return BILLING_PLAN_KEYS.flatMap((plan) =>
    HEADCOUNT_TIERS.flatMap((upTo) => BILLING_PERIODS.map((period) => ({ plan, upTo, period }))));
}

/** 서버가 RevenueCat 에서 다시 읽어 저장한 구독 한 줄. 상태 판정은 만료 시각 하나다. */
export interface StoreSubscriptionDto extends StoreProduct {
  readonly productId: string;
  readonly store: 'app_store' | 'play_store' | 'other';
  readonly expiresAt: string;
  /** 해지했지만 기간이 남은 상태면 false — 혜택은 만료까지 그대로다. */
  readonly willRenew: boolean;
  readonly sandbox: boolean;
}

/**
 * 오너가 보는 구독 상태. **막지 않는다**(2026-10-09 소유자 결정 — 초과는 알리기만): `covered: false` 는
 * 화면이 "구간을 올리세요"를 말할 근거일 뿐, 어느 문도 이것으로 닫히지 않는다.
 */
export interface BillingStatusDto {
  /** RevenueCat 의 app user id. **회사가 구독의 주인이다** — 직원 한 사람이 아니다. */
  readonly appUserId: string;
  /** 스토어 결제가 이 서버에 연결돼 있는가. `none` 이면 앱이 결제 화면 대신 그 사실을 말한다. */
  readonly provider: 'revenuecat' | 'none';
  readonly headcount: number;
  /** 재직 인원이 무료 구간(`FREE_HEADCOUNT`) 안인가. */
  readonly free: boolean;
  /** 지금 인원을 덮는 가장 작은 구간. 50명을 넘으면 null(상담). */
  readonly recommendedTier: HeadcountTier | null;
  /** 만료되지 않은 구독. */
  readonly active: readonly StoreSubscriptionDto[];
  /** 켜져 있는데 구독이 덮지 않는 유료 모듈(무료 구간을 넘었을 때만 의미가 있다). */
  readonly uncoveredModules: readonly string[];
  /** 산 구간이 지금 인원을 덮는가. 무료 구간 안이면 true. */
  readonly covered: boolean;
  readonly syncedAt: string | null;
}
